import React, { useRef, useEffect, useState } from "react";
import { Formik, Field, isEmptyArray } from "formik";
import Form, { InputSection } from "../components/Form";
import { DropDown } from "../../../ui/DropDown";
import Input from "../../../ui/Input";
import { parseDropdown } from "../../../utils/parseDropdown";
import { postEndpoint, putEndpoint } from "../../../api/api";
import { get } from "../../../utils/get";
import { MATRIX_URL } from "../../../api/urls";
import { TitleText } from "../../../ui/Text";
import { CloseIcon2 } from "../../../assets/svg/CloseIcon2";
import styled from "styled-components";
import Modal from "../../../ui/Modal";
import { ScorecardIcon } from "../../../assets/svg/ScorecardIcon";
import { OrganisationTypeIcon } from "../../../assets/svg/OrganisationTypeIcon";
import { SettingsIcon } from "../../../assets/svg/SettingsIcon";
import { Button } from "../../../ui/Button";
import { toast } from "react-toastify";
import { MatrixSettingsSchema } from "../../../utils/validationSchema/MatrixSettingsSchema";
import { KPIGlobalSettings } from "../GlobalSettings/KPIGlobalSettings";
import { CircleIcon } from "../../../assets/svg/CircleIcon";
import useAuth from "../../../hooks/useAuth";

interface IProps {
  closeModal?: any;
  setHandleSubmit?: any;
  setScorecards?: any;
  periods?: any;
  editData?: any;
  edit?: any;
  isModalOpen?: any;
  step?: any;
  setStep?: any;
}

const SettingsForm = ({
  closeModal,
  setHandleSubmit,
  isModalOpen,
  editData,
  edit,
  step,
  setStep,
}: IProps) => {
  const [scoringMatrix, setScoringMatrix] = useState<any>([]);
  const [createRes, createReq] = postEndpoint();
  const [updateRes, updateReq] = putEndpoint();
  const formRef: any = useRef<any>(null);

  const { id } = editData;

  const { user } = useAuth().authState;
  const userId = get(user, "id");

  const isAddMode = !id;

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  useEffect(() => {
    setHandleSubmit(() => handleSubmit);
    return () => {};
  }, []);

  const toggleModal = () => {
    setScoringMatrix([]);
    formRef.current.resetForm({ values: "" });
    closeModal();
  };

  const AddSettings = (values, { resetForm }) => {
    const payload = {
      name: get(values, "name"),
      notes: get(values, "notes"),
      staff_id:  userId,
      scoring_matrix_items: scoringMatrix,
    };
    createReq(MATRIX_URL, payload);
    resetForm({ values: "" });
  };

  const EditSettings = (values, { resetForm }) => {
    const payload = {
      name: get(values, "name"),
      notes: get(values, "notes"),
      staff_id:  userId,
      scoring_matrix_items: scoringMatrix,
    };
    updateReq(`${MATRIX_URL}/${id}`, payload);
    resetForm({ values: "" });
  };

  useEffect(() => {
    if (!isAddMode) {
      setScoringMatrix(get(editData, "scoring_matrix_items"));
      const newEditData = {
        ...editData,
        notes:get(editData, "notes"),
        // period_id: {
        //   value: get(editData, "period_id"),
        //   label: get(editData, "period.name"),
        // },
        // review_cycle: {
        //   value: get(editData, "review_cycle"),
        //   label: get(editData, "review_cycle"),
        // },
      };
      const fields = [
        "notes",
        "name",
        // "max_score",
        // "target_score",
        // "period_id",
        // "min_score",
        // "respondents",
        // "review_cycle",
        // "review_start_days",
      ];

      fields.forEach((field) =>
        formRef.current.setFieldValue(field, newEditData[field], false)
      );
      
    }
  }, [isAddMode]);

  useEffect(() => {
    const { complete, pending, data, error } = createRes;
    if (error) toast.error(get(data, "message"));
    if (!pending && complete && !error) {
      toast.success("Settings configured successfully");
      toggleModal();
    }
  }, [createRes]);

  useEffect(() => {
    const { complete, pending, data, error } = updateRes;
    if (error) toast.error(get(data, "message"));
    if (!pending && complete && !error) {
      toast.success("Settings Updated successfully");
      toggleModal();
    }
  }, [updateRes]);

  const nextStep = () => {
    setStep(step + 1);
  };
  const previousStep = () => {
    setStep(step - 1);
  };

  const renderForms = (values) => {
    const scoringMetrixOptions = [
      {
        value: "below_target",
        label: "Below Target",
      },
      {
        value: "above_target",
        label: "Above Target",
      },
      {
        value: "equal_target",
        label: "Equal to Target",
      },
    ];
    const AddScoreMatrix = () => {
      if (get(values, "min") && get(values, "max") && get(values, "score")) {
        const obj = {
          score: parseInt(get(values, "score")),
          min_threshold: parseInt(get(values, "min")),
          max_threshold: parseInt(get(values, "max")),
        };

        setScoringMatrix([...scoringMatrix, obj]);
      }
    };
    const deleteScoring = (index) => {
      setScoringMatrix(
        scoringMatrix.filter(
          (score, i) => scoringMatrix[i] !== scoringMatrix[index]
        )
      );
    };

    switch (step) {
      case 1:
        return (
          <Form>
            <InputSection>
              <Field
                type="text"
                name="name"
                placeholder="enter name"
                label="Name"
                component={Input}
              />
            </InputSection>
            <InputSection>
              <Field
                name="notes"
                placeholder="description"
                label="Description"
                type="textarea"
                component={Input}
                width="96%"
                height="6em"
                isBordered={true}
              />
            </InputSection>
          </Form>
        );
      case 2:
        return (
          <Form>
            <InputSection>
              <Field
                name="scoring_function"
                dropdownLabel="Scoring Function"
                component={DropDown}
                options={[{ label: "Custom", value: "Custom" }]}
                isBordered={true}
                width="97%"
                backgroundColor="#F5F6FB"
              />
            </InputSection>
            <InputSection>
              <ScoringSectionHeader>
                <TitleText>Min</TitleText>
                <TitleText>Max</TitleText>
                <TitleText>Score</TitleText>
                <TitleText style={{ color: "#01A8CE" }} onClick={AddScoreMatrix}>
                  Add +
                </TitleText>
              </ScoringSectionHeader>

              <ScoringSection>
                {scoringMatrix &&
                  scoringMatrix.map((i, index) => (
                    <>
                      <TitleText>{i.min_threshold}%</TitleText>
                      <TitleText>{i.max_threshold}%</TitleText>
                      <TitleText>{i.score}</TitleText>
                      <span onClick={() => deleteScoring(index)}>
                        <CloseIcon2 />
                      </span>
                    </>
                  ))}
                <>
                  <Field
                    name="min"
                    component={Input}
                    width="36%"
                    isBordered={true}
                  />
                  <Field
                    name="max"
                    component={Input}
                    width="36%"
                    isBordered={true}
                  />

                  <Field
                    name="score"
                    component={Input}
                    width="36%"
                    isBordered={true}
                  />
                </>
              </ScoringSection>
            </InputSection>
          </Form>
        );
    }
  };
  const initialValues = {
    name: "",
  };
  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={MatrixSettingsSchema}
      onSubmit={isAddMode ? AddSettings : EditSettings}
    >
      {({ values }) => (
        <>
          {isModalOpen && (
            <Modal
              title={edit ? "Edit Matrix" : "Add new Matrix"}
              handleSubmit={handleSubmit}
              closeModal={toggleModal}
              noFooter
            >
              <WizzardHeader>
                <HeaderItem enabled={step === 1 ? true : false}>
                  <ScorecardIcon isActive />
                  <TitleText>Overview</TitleText>
                </HeaderItem>
                <HeaderItem enabled={step === 2 ? true : false}>
                  <CircleIcon isActive />
                  <TitleText>Scoring Matrix</TitleText>
                </HeaderItem>
              </WizzardHeader>
              {renderForms(values)}
              <Footer>
                <Button text="Cancel" backgroundColor="#fff" color="#214160" />
                <ButtonWithBorder
                  disabled={step === 1 ? true : false}
                  text="Back"
                  onClick={previousStep}
                  backgroundColor="#fff"
                  color="#214160"
                />
                {step === 2 ? (
                  <SmallButton
                    text="Finish"
                    type="submit"
                    onClick={() => handleSubmit()}
                  />
                ) : (
                  <SmallButton text="Next" onClick={nextStep} />
                )}
              </Footer>
            </Modal>
          )}
        </>
      )}
    </Formik>
  );
};

export default SettingsForm;

const ScoringContainer = styled.div`
  display: grid;
  grid-template-columns: 7fr 1fr;
`;

const ScoringSectionHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 1fr;
  ${TitleText} {
    color: #c4c9fe;
  }
`;

const ScoringSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 1fr;
  align-items: center;
  ${TitleText} {
    font-weight: normal;
    opacity: 0.9;
  }
`;

const WizzardHeader = styled.div`
  display: flex;
  flex-direction: row;
  /* justify-content: space-between; */
  align-items: center;
  border: 2px solid rgba(76, 92, 251, 0.1);
  border-radius: 1em;
  margin-top: 1em;
  margin-bottom: 1em;
`;

const HeaderItem = styled.button<{ enabled: boolean }>`
  display: flex;
  font-size: 12;
  font-weight: 600;
  align-items: center;
  background-color: ${(props) => (props.enabled ? "#EBEFFF" : "#fff")};
  color: #01A8CE;
  padding: 1em 1.5em 1em 1.5em;
  border-radius: 1em;
  width: 100%;
  box-shadow: 0px 0px 0px transparent;
  border: 1px solid transparent;
  text-shadow: 0px 0px 0px transparent;
  border-color: ${(props) =>
    props.enabled ? "rgba(1,168,206,1)" : "none"};
  opacity: ${(props) => (props.enabled ? 1 : 0.4)};
  ${TitleText} {
    padding-left: 1em;
    color: #01A8CE;
    font-size: 12px;
  }
  &:focus {
    outline: none;
  }
`;

const ButtonWithBorder = styled(Button)<{ disabled: boolean }>`
  border: 2px solid #01A8CE;
  padding: 0.3em 4em;
  margin-left: auto;
  margin-right: 1em;
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
`;

const SmallButton = styled(Button)`
  padding: 0.4em 4em;
  border: 2px solid #01A8CE;
`;

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1em;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  ${TitleText} {
    padding-left: 1em;
    color: #01A8CE;
    font-size: 12px;
  }
`;
